import React from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { ChevronDown, Home, Briefcase, Truck, Brain, Shield, Compass } from 'lucide-react';
import NotificationCenter from './NotificationCenter';
import MobileNav from './MobileNav';
import GlobalSearch from './GlobalSearch';
import { integrationService } from '../services/api';

const DashboardLayout = ({ children }) => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [integrationOn, setIntegrationOn] = React.useState(false);

  React.useEffect(() => {
    let active = true;
    integrationService.isEnabled().then((on) => { if (active) setIntegrationOn(on); }).catch(() => {});
    return () => { active = false; };
  }, []);

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const isAdmin = user?.role === 'admin' || user?.role === 'super_admin' || user?.role === 'safety_compliance_admin' || user?.role === 'payroll_admin' || user?.role === 'ops_admin';
  const isAdminPage = location.pathname.startsWith('/admin');
  const superOrOps = user?.role === 'super_admin' || user?.role === 'ops_admin';

  // Founder/Admin information architecture: HOME · BUSINESS · OPERATIONS · INTELLIGENCE · ADMIN.
  // Every item reuses an EXISTING route — nothing is duplicated. Integration/Drive items only
  // appear when the relationship/integration layer is enabled.
  const navGroups = [
    {
      label: 'Attention', testid: 'nav-group-attention', items: [
        { to: '/admin/attention', label: 'Founder Attention', testid: 'nav-admin-attention' },
      ],
    },
    {
      label: 'Operations', testid: 'nav-group-operations', items: [
        { to: '/admin/loads', label: 'Jobs / Loads', testid: 'nav-admin-loads' },
        { to: '/admin/quote-builder', label: 'Quote Builder', testid: 'nav-admin-quote-builder' },
        { to: '/admin/dispatch', label: 'Dispatch', testid: 'nav-admin-dispatch' },
        { to: '/admin/dashboard', label: 'Driver Operations', testid: 'nav-admin-driver-ops' },
        { to: '/operations/capability-check', label: 'Equipment Capability', testid: 'nav-operations-capability' },
        { to: '/operations/history', label: 'Operational History', testid: 'nav-operations-history' },
      ],
    },
    {
      label: 'Business', testid: 'nav-group-business', items: [
        { to: '/admin/leads', label: 'Customers & Leads', testid: 'nav-admin-leads' },
        { to: '/admin/partner-requests', label: 'Partner Requests', testid: 'nav-admin-partner-requests' },
        { to: '/admin/partners', label: 'Partner Accounts', testid: 'nav-admin-partners' },
        ...(integrationOn ? [{ to: '/admin/customer-360', label: 'Customer 360', testid: 'nav-admin-customer360' }] : []),
        { to: '/admin/sales-intelligence', label: 'Sales', testid: 'nav-admin-sales-intelligence' },
        { to: '/admin/communications', label: 'Outreach / Communications', testid: 'nav-admin-communications' },
      ],
    },
    {
      label: 'People', testid: 'nav-group-people', items: [
        { to: '/admin/people', label: 'People & Companies', testid: 'nav-admin-people' },
        { to: '/applicants', label: 'Applicants', testid: 'nav-admin-applicants' },
        { to: '/operations', label: 'Applicant Intelligence', testid: 'nav-admin-applicant-intelligence' },
        { to: '/onboarding', label: 'Driver Onboarding', testid: 'nav-admin-onboarding' },
        { to: '/admin/staff-onboarding', label: 'Staff Setup', testid: 'nav-admin-staff-onboarding' },
        ...(superOrOps ? [{ to: '/admin/staff-directory', label: 'Staff Directory', testid: 'nav-admin-staff-directory' }] : []),
        { to: '/admin/jobs', label: 'Recruiting / Job Drafts', testid: 'nav-admin-jobs' },
        { to: '/admin/activities', label: 'Follow-Up Activities', testid: 'nav-admin-activities' },
      ],
    },
    {
      label: 'Finance', testid: 'nav-group-finance', items: [
        { to: '/admin/accounting', label: 'Settlements', testid: 'nav-admin-settlements' },
      ],
    },
    {
      label: 'Compliance & Documents', testid: 'nav-group-compliance', items: [
        { to: '/admin/documents', label: 'Documents', testid: 'nav-admin-documents' },
      ],
    },
    {
      label: 'Admin & System', testid: 'nav-group-admin', items: [
        { to: '/admin/users', label: 'All Users', testid: 'nav-admin-users' },
        ...(integrationOn ? [{ to: '/admin/integration', label: 'Integration Console', testid: 'nav-admin-integration' }] : []),
        { to: '/admin/analytics', label: 'Analytics', testid: 'nav-admin-analytics' },
        { to: '/admin/audit-logs', label: 'Audit Logs', testid: 'nav-admin-audit-logs' },
        { to: '/admin/activation-status', label: 'Activation Status', testid: 'nav-admin-activation-status' },
        ...(superOrOps ? [
          { to: '/admin/feature-toggles', label: 'Feature Controls', testid: 'nav-admin-feature-toggles' },
          { to: '/admin/site-content', label: 'Site Content', testid: 'site-content-nav-link' },
          { to: '/admin/user-activity', label: 'User Activity', testid: 'nav-admin-user-activity' },
        ] : []),
        ...(user?.role === 'super_admin' ? [
          { to: '/admin/founder-maintenance', label: 'Founder Maintenance', testid: 'nav-admin-founder-maintenance' },
        ] : []),
      ],
    },
  ];

  return (
    <div className="min-h-screen bg-gray-100 overflow-x-hidden">
      {/* Navigation */}
      <nav className="bg-white shadow-sm sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between h-14 sm:h-16 items-center">
            {/* Logo */}
            <div className="flex items-center">
              <Link to="/dashboard" className="flex items-center gap-2">
                <div className="w-8 h-8 sm:w-10 sm:h-10 bg-blue-600 rounded-lg flex items-center justify-center">
                  <span className="text-white font-bold text-lg sm:text-xl">B</span>
                </div>
                <h1 className="text-lg sm:text-xl lg:text-2xl font-bold text-blue-900 hidden sm:block">
                  BlueLock Freights
                </h1>
                <h1 className="text-lg font-bold text-blue-900 sm:hidden">
                  BlueLock
                </h1>
              </Link>
            </div>

            {/* Desktop Navigation */}
            <div className="hidden lg:flex items-center space-x-4">
              <Link
                to="/dashboard"
                className={`text-sm font-medium transition-colors ${
                  location.pathname === '/dashboard' 
                    ? 'text-blue-600' 
                    : 'text-gray-600 hover:text-gray-900'
                }`}
                data-testid="nav-home"
              >
                Home
              </Link>
              <Link
                to="/hub"
                className={`text-sm font-medium transition-colors ${
                  location.pathname === '/hub'
                    ? 'text-blue-600'
                    : 'text-gray-600 hover:text-gray-900'
                }`}
                data-testid="nav-bluelock-hub"
              >
                Company Hub
              </Link>
              {!isAdmin && (
                <Link
                  to="/my-documents"
                  className={`text-sm font-medium transition-colors ${
                    location.pathname === '/my-documents'
                      ? 'text-blue-600'
                      : 'text-gray-600 hover:text-gray-900'
                  }`}
                  data-testid="nav-my-documents"
                >
                  My Documents
                </Link>
              )}
              {isAdmin && navGroups.map((group) => (
                <div key={group.label} className="relative group" data-testid={group.testid}>
                  <button
                    type="button"
                    className={`text-sm font-medium transition-colors flex items-center gap-1 ${
                      group.items.some((l) => location.pathname === l.to)
                        ? 'text-blue-600'
                        : 'text-gray-600 hover:text-gray-900'
                    }`}
                  >
                    {group.label} <ChevronDown className="w-4 h-4" />
                  </button>
                  <div className="absolute right-0 top-full pt-2 hidden group-hover:block z-50">
                    <div className="bg-white rounded-lg shadow-lg border border-gray-100 py-2 w-56">
                      {group.items.map((l) => (
                        <Link
                          key={l.to + l.label}
                          to={l.to}
                          {...(l.testid ? { 'data-testid': l.testid } : {})}
                          className={`block px-4 py-2 text-sm transition-colors ${
                            location.pathname === l.to
                              ? 'text-blue-600 bg-blue-50'
                              : 'text-gray-700 hover:bg-gray-50'
                          }`}
                        >
                          {l.label}
                        </Link>
                      ))}
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {/* Right side - Notifications, User, Menu */}
            <div className="flex items-center gap-2 sm:gap-4">
              {/* Universal Enterprise Search (flag-gated; hidden when OFF) */}
              <GlobalSearch />

              {/* Notifications */}
              <NotificationCenter />

              {/* User Info - Desktop */}
              <div className="hidden sm:flex items-center gap-4 min-w-0">
                <div className="text-right min-w-0 max-w-[160px]">
                  <p className="text-sm font-medium text-gray-900 truncate">
                    {user?.first_name} {user?.last_name}
                  </p>
                  <p className="text-xs text-gray-500 truncate">
                    {/* Display custom role label if set, otherwise format the role */}
                    {user?.display_role_label || user?.role?.replace(/_/g, ' ')}
                    {user?.display_subtitle && (
                      <span className="text-gray-400 ml-1">• {user.display_subtitle}</span>
                    )}
                  </p>
                  {/* BUILD_ID: Super Admin only - for cache verification */}
                  {user?.role === 'super_admin' && (
                    <p className="text-xs text-green-600 font-mono" data-testid="debug-build-id" title="Build ID for cache verification">
                      Build: {window.__BLUELOCK_BUILD_ID__ || 'N/A'}
                    </p>
                  )}
                </div>
                <Link
                  to="/settings"
                  className="text-sm text-gray-600 hover:text-gray-900 px-3 py-1.5 rounded-md hover:bg-gray-100 transition-colors"
                  data-testid="settings-btn"
                >
                  Settings
                </Link>
                <button
                  onClick={handleLogout}
                  className="text-sm text-gray-600 hover:text-gray-900 px-3 py-1.5 rounded-md hover:bg-gray-100 transition-colors"
                  data-testid="logout-btn"
                >
                  Logout
                </button>
              </div>

              {/* Mobile Menu */}
              <MobileNav />
            </div>
          </div>
        </div>
      </nav>

      {/* Breadcrumb for Admin Pages */}
      {isAdminPage && (
        <div className="bg-white border-b border-gray-200">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-2">
            <nav className="flex text-sm" aria-label="Breadcrumb">
              <Link to="/dashboard" className="text-gray-500 hover:text-gray-700">
                Dashboard
              </Link>
              <svg className="w-4 h-4 text-gray-400 mx-2" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z" clipRule="evenodd" />
              </svg>
              <span className="text-gray-900 font-medium">Admin Panel</span>
            </nav>
          </div>
        </div>
      )}

      {/* Main Content */}
      <main className="max-w-7xl mx-auto py-4 sm:py-6 px-4 sm:px-6 lg:px-8">
        {children}
      </main>

      {/* Mobile Bottom Navigation — role-specific */}
      <div className="lg:hidden fixed bottom-0 left-0 right-0 bg-white border-t border-gray-200 z-40">
        {isAdmin ? (
          <div className="flex justify-around items-center h-16" data-testid="mobile-bottom-nav-admin">
            {[
              { to: '/dashboard', label: 'Home', Icon: Home, active: ['/dashboard', '/admin/attention'].includes(location.pathname), testid: 'mobile-bottom-home' },
              { to: '/admin/leads', label: 'Business', Icon: Briefcase, active: ['/admin/leads', '/admin/quote-builder', '/admin/customer-360', '/admin/sales-intelligence', '/admin/communications'].includes(location.pathname), testid: 'mobile-bottom-business' },
              { to: '/admin/dashboard', label: 'Ops', Icon: Truck, active: ['/admin/dashboard', '/admin/dispatch', '/admin/loads', '/admin/documents', '/admin/accounting', '/admin/people', '/onboarding', '/operations/history', '/operations/capability-check'].includes(location.pathname), testid: 'mobile-bottom-operations' },
              { to: integrationOn ? '/admin/integration' : '/admin/analytics', label: 'Intel', Icon: Brain, active: ['/admin/integration', '/admin/analytics', '/admin/audit-logs', '/admin/activation-status'].includes(location.pathname), testid: 'mobile-bottom-intelligence' },
              { to: '/applicants', label: 'Admin', Icon: Shield, active: ['/applicants', '/operations', '/admin/jobs', '/admin/staff-onboarding', '/admin/users', '/admin/feature-toggles', '/admin/staff-directory', '/admin/site-content', '/admin/user-activity'].includes(location.pathname), testid: 'mobile-bottom-admin' },
            ].map(({ to, label, Icon, active, testid }) => (
              <Link key={label} to={to} data-testid={testid} className={`flex flex-col items-center justify-center flex-1 h-full ${active ? 'text-blue-600' : 'text-gray-500'}`}>
                <Icon className="w-5 h-5" />
                <span className="text-[10px] mt-1">{label}</span>
              </Link>
            ))}
          </div>
        ) : (
          <div className="flex justify-around items-center h-16" data-testid="mobile-bottom-user">
            {[
              { to: '/dashboard', label: 'Home', Icon: Home, active: location.pathname === '/dashboard', testid: 'mobile-bottom-home' },
              { to: '/hub', label: 'Hub', Icon: Compass, active: location.pathname === '/hub', testid: 'mobile-nav-hub' },
              { to: '/my-documents', label: 'Documents', Icon: Briefcase, active: location.pathname === '/my-documents', testid: 'mobile-bottom-mydocs' },
              { to: '/settings', label: 'Settings', Icon: Shield, active: location.pathname === '/settings', testid: 'mobile-bottom-settings' },
            ].map(({ to, label, Icon, active, testid }) => (
              <Link key={label} to={to} data-testid={testid} className={`flex flex-col items-center justify-center flex-1 h-full ${active ? 'text-blue-600' : 'text-gray-500'}`}>
                <Icon className="w-5 h-5" />
                <span className="text-[10px] mt-1">{label}</span>
              </Link>
            ))}
          </div>
        )}
      </div>

      {/* Spacer for mobile bottom nav */}
      <div className="lg:hidden h-16"></div>
    </div>
  );
};

export default DashboardLayout;
